import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Clock, 
  User, 
  FileText, 
  Shield, 
  AlertTriangle, 
  Download, 
  RefreshCw, 
  Search
} from 'lucide-react';

// Common Components
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';

// Services
import AuditService from '../../services/auditService';

/**
 * AuditTrails Component
 * Displays a filtered, paginated list of system audit logs.
 * Supports exporting data to CSV.
 */
const AuditTrails = () => {
  // --- Hooks & State ---
  const { success, error: showError, toasts, removeToast } = useToast();
  
  const [activeTab, setActiveTab] = useState('activity-logs'); // Controls the active filter view
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  
  // Pagination State
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });

  // --- Configuration ---

  // Tab Definitions - Mapped to backend filters
  const tabs = useMemo(() => [
    { id: 'activity-logs', name: 'All Activity', icon: <Clock size={18} />, filter: {} },
    { id: 'change-history', name: 'Modifications', icon: <FileText size={18} />, filter: { action: 'UPDATE' } },
    { id: 'compliance-tracking', name: 'Access Logs', icon: <Shield size={18} />, filter: { action: 'ACCESS' } },
    { id: 'system-events', name: 'System Events', icon: <AlertTriangle size={18} />, filter: { entityType: 'System' } }
  ], []);

  // UI Helper: Get color badges for different actions
  const getActionBadgeClass = (action) => {
    switch (action) {
      case 'CREATE': return 'bg-green-100 text-green-700 border-green-200';
      case 'UPDATE': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'DELETE': return 'bg-red-100 text-red-700 border-red-200';
      case 'ACCESS': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'LOGIN': return 'bg-purple-100 text-purple-700 border-purple-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // --- API Actions ---

  /**
   * Fetches audit logs from the backend based on current tab and pagination.
   */
  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Find current tab config to get specific filters
      const currentTabConfig = tabs.find(t => t.id === activeTab) || tabs[0];
      
      // 2. Construct Query Params
      const queryParams = { 
        page: pagination.page, 
        limit: pagination.limit,
        ...currentTabConfig.filter
      };

      // 3. API Call
      const response = await AuditService.getLogs(queryParams);
      
      if (response.success) {
        setLogs(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          pages: response.pagination.pages
        }));
      }
    } catch (err) {
      console.error('Audit Log Load Error:', err);
      showError('Failed to load audit logs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeTab, pagination.page, pagination.limit]);

  // Initial Load & Refresh on dependency change
  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  /**
   * Triggers the CSV export download.
   */
  const handleExport = async () => {
    try {
      success('Export started. Downloading CSV...');
      await AuditService.exportLogs();
    } catch (err) {
      console.error('Export Error:', err);
      showError('Failed to export logs.');
    }
  };

  /**
   * Handles Tab Switching.
   * Resets pagination to page 1 when switching views.
   */
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // --- Table Column Definitions ---
  const columns = useMemo(() => [
    {
      key: 'timestamp',
      label: 'Timestamp',
      width: '180px',
      render: (value, row) => (
        <span className="text-gray-600 font-mono text-xs">
          {new Date(row.timestamp).toLocaleString()}
        </span>
      )
    },
    {
      key: 'user',
      label: 'User',
      width: '220px',
      render: (value, row) => {
        const user = row.userId;
        const initial = user?.firstName ? user.firstName.charAt(0) : 'S';
        const name = user ? `${user.firstName} ${user.lastName}` : 'System';
        const email = user?.email || 'Automated Process';

        return (
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${user ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-200 text-gray-600'}`}>
              {initial}
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">{name}</div>
              <div className="text-xs text-gray-500">{email}</div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'action',
      label: 'Action',
      width: '120px',
      render: (value, row) => (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getActionBadgeClass(row.action)}`}>
          {row.action}
        </span>
      )
    },
    {
      key: 'entityType',
      label: 'Resource',
      width: '180px',
      render: (value, row) => (
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-700">{row.entityType}</span>
          {row.entityId && (
            <span className="text-xs text-gray-400 font-mono" title={row.entityId}>
              ID: {row.entityId.substring(0, 8)}...
            </span>
          )}
        </div>
      )
    },
    {
      key: 'details',
      label: 'Details',
      render: (value, row) => (
        <span className="text-sm text-gray-600 truncate max-w-xs block" title={row.details}>
          {row.details || '-'}
        </span>
      )
    },
    {
      key: 'ipAddress',
      label: 'IP Address',
      width: '140px',
      render: (value, row) => (
        <span className="text-xs text-gray-500 font-mono bg-gray-50 px-2 py-1 rounded">
          {row.ipAddress}
        </span>
      )
    }
  ], []);

  // --- Header Actions ---
  const headerActions = [
    {
      label: 'Refresh',
      icon: <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />,
      onClick: loadLogs,
      variant: 'outline',
      disabled: loading
    },
    {
      label: 'Export CSV',
      icon: <Download size={16} />,
      onClick: handleExport,
      variant: 'primary'
    }
  ];

  return (
    <div className="page-container">
      {/* 1. Header Section */}
      <PageBreadcrumb />
      <PageHeader 
        title="Audit Trails" 
        description="Immutable record of all system activities, data access, and security events."
        actions={headerActions}
      />

      <div className="w-full px-8 pb-8">
        
        {/* 2. Tab Navigation */}
        <div className="flex border-b border-gray-200 mb-6 gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`
                flex items-center gap-2 pb-3 text-sm font-medium transition-colors relative
                ${activeTab === tab.id 
                  ? 'text-cyan-700 border-b-2 border-cyan-700' 
                  : 'text-gray-500 hover:text-gray-700'
                }
              `}
            >
              <span className={activeTab === tab.id ? 'text-cyan-700' : 'text-gray-400'}>
                {tab.icon}
              </span>
              {tab.name}
            </button>
          ))}
        </div>

        {/* 3. Main Data Table */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <DataTable
            data={logs}
            columns={columns}
            loading={loading}
            pagination={{
              currentPage: pagination.page,
              totalPages: pagination.pages,
              totalItems: pagination.total,
              itemsPerPage: pagination.limit
            }}
            onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
            emptyMessage="No audit logs found for the selected criteria."
            showActions={false} // Audit logs are read-only by design
            searchable={false}  // Search is handled by backend if needed, simpler to omit for now
          />
        </div>
      </div>

      {/* 4. Global Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default AuditTrails;